import { globalStore, isAllowed } from './../../../shared/shared.global-service';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    signal,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { LayoutService } from '../../services/layout.service';
import { Router, RouterLink } from '@angular/router';
import { TopbarStore } from './topbar.store';
import { SubscriptionComponent } from '../../../shared/helpers/subscription.component';
import { isOffline } from '../../../../utils/connection.service';
import { OfflineDb } from '../../../../store/offline-db/offline.db';
import { BaseDialogService } from '../../../../components/kit/base-dialog/base-dialog-service';
import { ConfigComponent } from '../config/config.component';
import { OrganizationYearSelectComponent } from '../organization-year-select/organization-year-select.component';
import {
    DelayedRequestsComponent,
} from '../../../../components/offline-db/delayed-requests/delayed-requests.component';
import { OrganizationSelectComponent } from '../organization-select/organization-select.component';
import { receiveNotification } from '../../store/notifications/notifications.actions';
import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { NotificationBellState } from './topbar.types';
import { NotificationSeverity } from '@libs/types/src/notifications/enum/notification-severity.enum';
import { NotificationsCenterComponent } from '../notification-center/notifications-center.component';
import { GuideComponent } from '../guide/guide.component';
import { IconComponent } from '../../../../components/kit/icon/icon.component';
import { featureOn } from 'packages/client/src/app/feature-flag';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreadcrumbService } from 'xng-breadcrumb';
import { normalizeGuidePageUrl } from '../../../../utils/router.utils';
import { AvatarComponent } from '../../../shared/components/kit/avatar/avatar.component';
import { BackgroundTaskStatus } from '@libs/types/src/notifications/enum/background-task.enum';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { StyleClass } from '@openng/optimus-ui/styleclass';
import { Ripple } from '@openng/optimus-ui/ripple';
import { ProgressBarModule } from '@openng/optimus-ui/progressbar';
import {
    SERVICE_SETTINGS_ATTENDANCE_DEFAULT,
    SERVICE_SETTINGS_ATTENDANCE_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import { getServiceSettings } from '../../../../utils/service.utils';
import {
    AttendanceCandidatesDialogComponent,
} from '../../../../components/social-service/attendance/attendance-candidates-dialog/attendance-candidates-dialog.component';

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    styleUrl: './topbar.component.scss',
    providers: [TopbarStore],
    imports: [
        OrganizationYearSelectComponent,
        DelayedRequestsComponent,
        TranslatePipe,
        StyleClass,
        Ripple,
        RouterLink,
        Tooltip,
        OrganizationSelectComponent,
        SidebarComponent,
        ProgressBarModule,
        IconComponent,
        AvatarComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [
        trigger('bellRing', [
            state('incoming', style({ transform: 'rotate(0deg)' })),
            transition('* => incoming', [
                animate(
                    '1000ms',
                    keyframes([
                        style({ transform: 'rotate(0deg)', offset: 0 }),
                        style({ transform: 'rotate(30deg)', offset: 0.1 }),
                        style({ transform: 'rotate(-30deg)', offset: 0.2 }),
                        style({ transform: 'rotate(30deg)', offset: 0.3 }),
                        style({ transform: 'rotate(-30deg)', offset: 0.4 }),
                        style({ transform: 'rotate(15deg)', offset: 0.5 }),
                        style({ transform: 'rotate(-15deg)', offset: 0.6 }),
                        style({ transform: 'rotate(5deg)', offset: 0.7 }),
                        style({ transform: 'rotate(-5deg)', offset: 0.8 }),
                        style({ transform: 'rotate(0deg)', offset: 1 }),
                    ]),
                ),
            ]),
        ]),
        trigger('badgePulse', [
            state('incoming', style({ transform: 'scale(1)' })),
            transition('* => incoming', [
                animate(
                    '1000ms',
                    keyframes([
                        style({ transform: 'scale(1)', offset: 0 }),
                        style({ transform: 'scale(1.3)', offset: 0.2 }),
                        style({ transform: 'scale(1)', offset: 0.4 }),
                        style({ transform: 'scale(1.2)', offset: 0.6 }),
                        style({ transform: 'scale(1)', offset: 1 }),
                    ]),
                ),
            ]),
        ]),
    ],
})
export class TopbarComponent extends SubscriptionComponent {
    @ViewChild('menubutton') menuButton!: ElementRef;
    @ViewChild('profileButton') profileButton!: ElementRef;
    @ViewChild(SidebarComponent) appSidebar!: SidebarComponent;

    public readonly store = inject(Store);
    public readonly topbarStore = inject(TopbarStore);
    public readonly translate = inject(TranslateService);
    public readonly router = inject(Router);
    public readonly layoutService = inject(LayoutService);
    public readonly dialogService = inject(BaseDialogService);
    private readonly breadcrumbService = inject(BreadcrumbService);

    public readonly offlineDb = inject(OfflineDb);

    private readonly breadcrumbs = toSignal(
        this.breadcrumbService.breadcrumbs$,
        { initialValue: [] },
    );

    user = computed(() =>
        this.topbarStore.globalGlobalStore.loggedUserStore.currentUser(),
    );
    notificationBellState = signal<NotificationBellState>({
        state: 'idle',
        color: 'var(--p-text-color)',
    });

    private readonly routerEvents = toSignal(this.router.events);

    protected readonly currentGuidePage = computed(() => {
        this.routerEvents();
        const url = normalizeGuidePageUrl(this.router.url);
        const pages =
            this.topbarStore.globalGlobalStore.guidePageStore.guidePages();

        return pages.find((p) => p.active && p.url === url) ?? null;
    });

    protected readonly attendanceServices = computed(() => {
        if (!featureOn.ATTENDANCE) return [];

        return this.topbarStore.globalGlobalStore.organizationStore.servicesInContext()
            .filter(service => {
                const settings = getServiceSettings(
                    service.serviceSettings,
                    SERVICE_SETTINGS_ATTENDANCE_TYPE_KEY,
                    SERVICE_SETTINGS_ATTENDANCE_DEFAULT,
                );
                const canReadPrescriptions = this.topbarStore.globalGlobalStore
                    .isAllowedForService('attendancePrescription.read', service.id)();
                const canCreateAttendance = this.topbarStore.globalGlobalStore
                    .isAllowedForService('attendance.create', service.id)();

                return (settings.useCard ?? true) && canReadPrescriptions && canCreateAttendance;
            });
    });

    constructor() {
        super();

        if (this.user()) {
            globalStore.dispatch(receiveNotification({ user: this.user()! }));
        }

        effect(() => {
            const notification =
                this.topbarStore.globalGlobalStore.loggedUserStore.newNotification();
            if (notification) this.triggerBellAnimation(notification.severity);
        });

        effect(() => {
            const task =
                this.topbarStore.backgroundTaskStore.latestAttentionTask();
            if (!task) return;
            const severity =
                task.status === BackgroundTaskStatus.COMPLETED
                    ? NotificationSeverity.SUCCESS
                    : task.status === BackgroundTaskStatus.EXPIRED
                        ? NotificationSeverity.WARNING
                        : NotificationSeverity.CRITICAL;
            this.triggerBellAnimation(severity);
        });
    }

    onMenuButtonClick(): void {
        this.layoutService.onMenuToggle();
    }

    onMicrophoneClick(): void {
        return;
    }

    onFullscreenClick(): void {
        return;
    }

    onSearchClick(): void {
        this.layoutService.toggleSearchBar();
    }

    protected openAttendanceCandidates(): void {
        const serviceIds = this.attendanceServices().map(service => service.id);
        if (!serviceIds.length) return;

        this.dialogService.open(AttendanceCandidatesDialogComponent, {
            header: this.translate.instant('attendance.daily.label'),
            styleClass: 'p-dialog-width-80',
            data: { serviceIds },
        });
    }

    onGuideClick(): void {
        const baseHeader = this.translate.instant('layout.guide.header');
        const pageName = this.getCurrentPageName();
        this.dialogService.open(GuideComponent, {
            header: pageName ? `${baseHeader}: ${pageName}` : baseHeader,
            styleClass: 'p-dialog-width-50',
        });
    }

    private getCurrentPageName(): string {
        const items = this.breadcrumbs();
        const last = items[items.length - 1];
        let label = typeof last?.label === 'string' ? last.label : '';

        if (label.includes('|')) {
            label = items[items.length - 2]?.label as string;
        }
        if (!label) return '';

        return this.translate.instant(`breadcrumbs.${label}`);
    }

    onProfileClick(): void {
        this.profileButton.nativeElement.click();
        this.router.navigateByUrl('/content/profile');
    }

    onSettingsClick(): void {
        this.dialogService.openDrawer(ConfigComponent, {
            header: this.translate.instant('layout.config.header'),
            styleClass: 'w-full drawer-35rem',
            contentStyle: {
                minHeight: '0',
                overflowY: 'auto',
            },
        });
    }

    onNotificationClick(): void {
        this.dialogService.openDrawer(NotificationsCenterComponent, {
            header: this.translate.instant('layout.notificationsCenter.header'),
            styleClass: 'w-full drawer-35rem',
        });
    }

    onBackgroundTasksClick(): void {
        this.onNotificationClick();
    }

    onEmailClick(): void {
        return;
    }

    onMyOfficeClick(): void {
        return;
    }

    onRequestsReportsClick(): void {
        return;
    }

    onReadMessagesClick(): void {
        return;
    }

    triggerBellAnimation(severity: NotificationSeverity | null = null) {
        this.playBellSound();
        this.notificationBellState.set({
            state: 'incoming',
            color: this.getNotificationBellStateColor(severity),
        });
    }

    onAnimationDone() {
        this.notificationBellState.set({
            state: 'idle',
            color: this.getNotificationBellStateColor(null),
        });
    }

    logout(): void {
        this.router.navigateByUrl('/login').then();
        this.topbarStore.globalGlobalStore.loggedUserStore.currentUser?.set(
            null,
        );
        this.topbarStore.globalGlobalStore.invalidateAcl();
        this.topbarStore.api.logout().then();
        this.offlineDb.clearAllDbs(true).then();
    }

    protected async refreshOfflineDbs(clear: boolean = true): Promise<void> {
        await this.offlineDb.refreshAllDbs(clear);
    }

    private getNotificationBellStateColor(
        severity: NotificationSeverity | null,
    ) {
        let color = 'var(--p-text-color)';

        if (severity) {
            switch (severity) {
                case NotificationSeverity.SUCCESS:
                    color = 'var(--p-button-success-background)';
                    break;
                case NotificationSeverity.WARNING:
                    color = 'var(--p-button-warning-background)';
                    break;
                case NotificationSeverity.CRITICAL:
                    color = 'var(--p-button-danger-background)';
                    break;
                case NotificationSeverity.INFO:
                default:
                    color = 'var(--p-button-info-background)';
                    break;
            }
        }

        return color;
    }

    private playBellSound() {
        const ctx = new window.AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1200, ctx.currentTime);
        gain.gain.setValueAtTime(0.5, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc.start();
        osc.stop(ctx.currentTime + 0.45);
    }

    protected readonly isAllowed = isAllowed;
    protected readonly isOffline = isOffline;
    protected readonly featureOn = featureOn;
}
